import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, FlaskConical, Activity, Pause, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn, formatNumber } from '@/lib/utils'
import { getPipelineTopology } from '../queries/pipelineTopology'
import { FlowCanvas, FlowLegend } from '../components/FlowCanvas'
import { ConnectorListModal } from '../components/ConnectorListModal'
import { fanOut, fmtNum } from '../components/flowGraph'
import type { FlowNodeSpec } from '../components/flowGraph'
import type {
  PipelineTopologyResponse,
  ConsumerGroupLag,
  TopicStageMetrics,
  ConnectorMetrics,
} from '../types/topology'

// ─── Demo data helpers ────────────────────────────────────────────────────────

function makeLag(
  groupId: string, label: string, lag: number, rate: number,
  status: ConsumerGroupLag['status']
): ConsumerGroupLag {
  const eta = lag === 0 ? 0 : rate > 0 ? Math.round((lag / rate) * 60) : null
  return { groupId, label, currentLag: lag, throughputPerMin: rate, etaSeconds: eta, status }
}
function makeTopic(stage: string, category: string, topic: string, partitions: number,
  hourly: number, consumers: ConsumerGroupLag[]): TopicStageMetrics {
  return { stage, category, topic, partitionCount: partitions, hourlyProducedCount: hourly, consumers }
}
function makeConnector(name: string, connectorType: string, category: string, state: string,
  totalTasks: number, runningTasks: number, lag: number, rate: number): ConnectorMetrics {
  const eta = lag === 0 ? 0 : rate > 0 ? Math.round((lag / rate) * 60) : null
  return { name, connectorType, category, state, totalTasks, runningTasks,
    currentLag: lag, throughputPerMin: rate, etaSeconds: eta,
    errorTrace: state === 'FAILED' ? 'ConnectException: Database connection failed\n\tat com.starrocks...' : null }
}

const DEMO: PipelineTopologyResponse = {
  fileService: { notStartedCount: 3, inProgressCount: 1, inProgressOldestAgeSec: 142, doneCount24h: 87, errorCount24h: 2 },
  rawStages: [
    makeTopic('raw', 'customer', 'customer-raw', 4, 182400, [makeLag('customer-raw-group', 'fh_raw_event_service', 0,    4200, 'healthy')]),
    makeTopic('raw', 'custom',   'custom-raw',   4, 9300,   [makeLag('custom-raw-group',   'fh_raw_event_service', 0,    950,  'healthy')]),
    makeTopic('raw', 'entity',   'entity-raw',   4, 9300,   [makeLag('entity-raw-group',   'fh_raw_event_service', 120,  950,  'lagging')]),
    makeTopic('raw', 'campaign', 'campaign-raw', 2, 1800,   [makeLag('campaign-raw-group', 'fh_raw_event_service', 0,    60,   'healthy')]),
    makeTopic('raw', 'datalist', 'datalist-raw', 8, 74100,  [makeLag('datalist-raw-group', 'fh_raw_event_service', 0,    2100, 'healthy')]),
  ],
  eventStages: [
    makeTopic('event', 'custom', 'custom-events', 4, 9300, [
      makeLag('connect-gcs-sink-events',           'gcs-sink-events',       0, 950,  'healthy'),
      makeLag('starrocks-custom-events-sink-group', 'sr-sink-custom-events', 0, 950,  'healthy'),
    ]),
    makeTopic('event', 'entity-update', 'entity-update-events', 16, 9300, [
      makeLag('entity-update-group', 'entity-update-group', 120, 950, 'lagging'),
    ]),
    makeTopic('event', 'campaign', 'campaign-events', 2, 14200, [
      makeLag('connect-gcs-sink-events',             'gcs-sink-events',          0, 480, 'healthy'),
      makeLag('starrocks-campaign-events-sink-group', 'sr-sink-campaign-events',  0, 480, 'healthy'),
    ]),
    makeTopic('event', 'datalist', 'datalist-events', 8, 74100, [
      makeLag('connect-gcs-sink-events',             'gcs-sink-events',          0, 2100, 'healthy'),
      makeLag('starrocks-datalist-events-sink-group', 'sr-sink-datalist-events',  0, 2100, 'healthy'),
    ]),
    makeTopic('event', 'customer-update', 'customer-update-events', 4, 182400, [
      makeLag('connect-gcs-sink-customer',     'gcs-sink-customer', 0, 4200, 'healthy'),
      makeLag('starrocks-customer-sink-group', 'sr-sink-customer',  0, 4200, 'healthy'),
    ]),
  ],
  journeyStages: [
    makeTopic('journey', 'journey-fg',      'journey-fg-events', 2, 3600,  [makeLag('journey-fg-group',      'fh_journey_service', 0,   60,  'healthy')]),
    makeTopic('journey', 'journey-bg',      'journey-bg-events', 4, 12420, [makeLag('journey-bg-group',      'fh_journey_service', 340, 207, 'lagging')]),
    makeTopic('journey', 'journey-updates', 'journey-updates',   1, 120,   [makeLag('journey-updates-group', 'fh_journey_service', 0,   2,   'healthy')]),
  ],
  gcsConnectors: [
    makeConnector('gcs-sink-events',        'gcs-sink', 'events',        'RUNNING', 1, 1, 120, 950),
    makeConnector('gcs-sink-customer',      'gcs-sink', 'customer',      'RUNNING', 1, 1, 0,   4200),
    makeConnector('gcs-sink-custom-entity', 'gcs-sink', 'custom-entity', 'RUNNING', 1, 1, 0,   320),
    makeConnector('gcs-sink-system-entity', 'gcs-sink', 'system-entity', 'RUNNING', 1, 1, 0,   1800),
    makeConnector('gcs-sink-fhid-mapping',  'gcs-sink', 'fhid-mapping',  'RUNNING', 1, 1, 0,   210),
  ],
  srConnectors: [
    makeConnector('starrocks-sink-campaign-events',         'sr-sink', 'campaign-events',         'RUNNING', 1,  1,  0,    480),
    makeConnector('starrocks-sink-custom-events',           'sr-sink', 'custom-events',           'RUNNING', 1,  1,  0,    950),
    makeConnector('starrocks-sink-datalist-events',         'sr-sink', 'datalist-events',         'RUNNING', 16, 16, 0,    2100),
    makeConnector('starrocks-sink-customer',                'sr-sink', 'customer',                'RUNNING', 4,  4,  0,    4200),
    makeConnector('starrocks-sink-system-transaction',      'sr-sink', 'system-transaction',      'FAILED',  16, 14, 5400, 0),
    makeConnector('starrocks-sink-system-transaction-item', 'sr-sink', 'system-transaction-item', 'RUNNING', 16, 16, 0,    1800),
  ],
  cdcSource: makeConnector('mongodb-cdc', 'mongodb-cdc', 'cdc-source', 'RUNNING', 1, 1, 0, -1),
  asOf: new Date().toISOString(),
}

// Journey demo (not in backend yet) — numeric values
const JOURNEY_DEMO = {
  queues:  [
    { label: 'jemail',    status: 'healthy', ratePerMin: 113, lag: 0, etaSec: 0 as number | null },
    { label: 'jsms',      status: 'healthy', ratePerMin: 53,  lag: 0, etaSec: 0 as number | null },
    { label: 'jrcs',      status: 'healthy', ratePerMin: 7,   lag: 0, etaSec: 0 as number | null },
    { label: 'jwhatsapp', status: 'healthy', ratePerMin: 30,  lag: 0, etaSec: 0 as number | null },
  ],
  deliveryTopics: [
    { label: 'email-delivery',    status: 'healthy', ratePerMin: 113, lag: 0, etaSec: 0 as number | null },
    { label: 'sms-delivery',      status: 'healthy', ratePerMin: 53,  lag: 0, etaSec: 0 as number | null },
    { label: 'rcs-delivery',      status: 'healthy', ratePerMin: 7,   lag: 0, etaSec: 0 as number | null },
    { label: 'whatsapp-delivery', status: 'healthy', ratePerMin: 30,  lag: 0, etaSec: 0 as number | null },
  ],
  messageStatus: { label: 'campaign-message-status', status: 'healthy', ratePerMin: 137, lag: 0, etaSec: 0 as number | null },
}

// ─── Metric helpers ───────────────────────────────────────────────────────────

/** Extract rate/lag/eta for a topic node — uses worst consumer's consume rate. */
function topicMetrics(t: TopicStageMetrics): { ratePerMin: number; lag: number; etaSec: number | null } {
  if (!t.consumers.length) return { ratePerMin: t.hourlyProducedCount / 60, lag: 0, etaSec: 0 }
  const worst = t.consumers.reduce((a, b) => a.currentLag > b.currentLag ? a : b)
  return { ratePerMin: worst.throughputPerMin, lag: worst.currentLag, etaSec: worst.etaSeconds }
}

function topicStatus(t: TopicStageMetrics | undefined): string {
  if (!t) return 'unknown'
  if (t.consumers.some(c => c.status === 'stalled')) return 'stalled'
  if (t.consumers.some(c => c.status === 'lagging')) return 'lagging'
  if (t.consumers.some(c => c.status === 'healthy')) return 'healthy'
  return 'unknown'
}

// ─── Node builders ────────────────────────────────────────────────────────────

function topicNode(
  t: TopicStageMetrics | undefined, fallback: string, col: number, go: (topic: string) => void
): FlowNodeSpec {
  if (!t) return { id: fallback, label: fallback, col, kind: 'placeholder' }
  const m = topicMetrics(t)
  return {
    id: t.topic, label: t.topic, col, kind: 'topic',
    status: topicStatus(t), ratePerMin: m.ratePerMin, lag: m.lag, etaSec: m.etaSec,
    sublabel: `${t.partitionCount} partitions`,
    onClick: () => go(t.topic),
  }
}

function connNode(c: ConnectorMetrics | undefined, fallback: string, col: number): FlowNodeSpec {
  if (!c) return { id: fallback, label: fallback, col, kind: 'placeholder' }
  return {
    id: c.name, label: c.name, col, kind: 'connector',
    status: c.state, ratePerMin: c.throughputPerMin, lag: c.currentLag, etaSec: c.etaSeconds,
    sublabel: `${c.runningTasks}/${c.totalTasks} tasks`,
    errorTrace: c.errorTrace,
  }
}

// Lookups take the snapshot explicitly so they stay pure and stable across renders.
const raw = (t: PipelineTopologyResponse | undefined, cat: string) => t?.rawStages.find(s => s.category === cat)
const evt = (t: PipelineTopologyResponse | undefined, cat: string) => t?.eventStages.find(s => s.category === cat)
const gcs = (t: PipelineTopologyResponse | undefined, cat: string) => t?.gcsConnectors.find(c => c.category === cat)
const sr  = (t: PipelineTopologyResponse | undefined, cat: string) => t?.srConnectors.find(c => c.category === cat)
const jny = (t: PipelineTopologyResponse | undefined, cat: string) => t?.journeyStages?.find(s => s.category === cat)

/** A plain demo node (journey stages have no backend metrics yet). */
function demoNode(
  d: { label: string; status: string; ratePerMin: number; lag: number; etaSec: number | null },
  col: number, go: (topic: string) => void
): FlowNodeSpec {
  return {
    id: d.label, label: d.label, col, kind: 'topic',
    status: d.status, ratePerMin: d.ratePerMin, lag: d.lag, etaSec: d.etaSec,
    onClick: () => go(d.label),
  }
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function PipelineShell({ title, badge, children }: {
  title: string; badge?: React.ReactNode; children: React.ReactNode
}) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {badge}
        <div className="flex-1 h-px bg-gray-200" />
      </div>
      {children}
    </div>
  )
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, variant = 'default', onClick }: {
  label: string; value: number | string; sub?: string; variant?: 'default' | 'ok' | 'warn' | 'error'
  onClick?: () => void
}) {
  const colors = { default: 'text-gray-900', ok: 'text-green-600', warn: 'text-yellow-600', error: 'text-red-600' }
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      className={cn(
        'bg-white border border-gray-200 rounded-lg p-4 min-w-[130px]',
        onClick && 'text-left hover:border-gray-300 hover:shadow-sm transition cursor-pointer'
      )}
    >
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={cn('text-2xl font-semibold tabular-nums', colors[variant])}>
        {typeof value === 'number' ? formatNumber(value) : value}
      </p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}{onClick && ' · view all'}</p>}
    </Tag>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PipelineTopologyPage() {
  const [demo, setDemo] = useState(true)
  const [demoAsOf, setDemoAsOf] = useState(() => Date.now())
  const [paused, setPaused] = useState(false)
  const [connectorsOpen, setConnectorsOpen] = useState(false)
  const navigate = useNavigate()
  const goTopic = useCallback(
    (topic: string) => navigate(`/pipeline-topology/topic/${encodeURIComponent(topic)}`),
    [navigate]
  )

  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['pipeline-topology'],
    queryFn: getPipelineTopology,
    refetchInterval: 30_000,
    enabled: !demo,
  })

  const t: PipelineTopologyResponse | undefined = demo ? DEMO : data
  // Falls back to mount time; irrelevant until a snapshot exists to count down from.
  const asOfMs = demo ? demoAsOf : (dataUpdatedAt || demoAsOf)

  const allConnectors = useMemo(
    () => t ? [...t.gcsConnectors, ...t.srConnectors, t.cdcSource] : [],
    [t]
  )
  /**
   * "Can't reach Kafka Connect" is not the same as "the connector is down".
   * The backend hands back state UNKNOWN with 0/0 tasks whenever its Connect
   * REST call fails, and the connector list itself is static — so a Connect
   * outage would otherwise read as every connector having failed at once.
   * Unknown is counted apart from failed and never turns the summary red.
   */
  const health = useMemo(() => {
    let running = 0, unknown = 0, failed = 0
    for (const c of allConnectors) {
      if (c.state === 'UNKNOWN') unknown++
      else if (c.state === 'RUNNING' && c.runningTasks >= c.totalTasks) running++
      else failed++
    }
    return { running, unknown, failed, total: allConnectors.length }
  }, [allConnectors])
  const allUnknown = health.total > 0 && health.unknown === health.total
  const totalRatePerMin = allConnectors.reduce((sum, c) => sum + Math.max(0, c.throughputPerMin), 0)
  const totalLag = useMemo(() => {
    if (!t) return 0
    const stageLag = [...t.rawStages, ...t.eventStages]
      .reduce((s, st) => s + st.consumers.reduce((a, c) => a + c.currentLag, 0), 0)
    return stageLag + allConnectors.reduce((s, c) => s + c.currentLag, 0)
  }, [t, allConnectors])

  /**
   * Each section is a small left-to-right DAG. Edges carry no metrics of their
   * own — FlowCanvas reads rate and status off the target node, so a lane is
   * drawn exactly as fast and as healthy as whatever is consuming it.
   */

  // custom-raw → custom-events → { GCS, StarRocks }
  const customGraph = useMemo(() => {
    const a = topicNode(raw(t, 'custom'), 'custom-raw', 0, goTopic)
    const b = topicNode(evt(t, 'custom'), 'custom-events', 1, goTopic)
    const sinks = [
      connNode(gcs(t, 'events'), 'gcs-sink-events', 2),
      connNode(sr(t, 'custom-events'), 'starrocks-sink-custom-events', 2),
    ]
    return { nodes: [a, b, ...sinks], edges: [{ from: a.id, to: b.id }, ...fanOut([b], sinks)] }
  }, [t, goTopic])

  // entity-raw → entity-update-events → MongoDB → CDC → { GCS entity, SR system-* }
  const entityGraph = useMemo(() => {
    const a = topicNode(raw(t, 'entity'), 'entity-raw', 0, goTopic)
    const b = topicNode(evt(t, 'entity-update'), 'entity-update-events', 1, goTopic)
    const store: FlowNodeSpec = { id: 'cdp_entity', label: 'cdp_entity', col: 2, kind: 'store', sublabel: 'MongoDB' }
    const cdc = connNode(t?.cdcSource, 'mongodb-cdc', 3)
    const sinks = [
      connNode(gcs(t, 'custom-entity'), 'gcs-sink-custom-entity', 4),
      connNode(gcs(t, 'system-entity'), 'gcs-sink-system-entity', 4),
      connNode(sr(t, 'system-transaction'), 'starrocks-sink-system-transaction', 4),
      connNode(sr(t, 'system-transaction-item'), 'starrocks-sink-system-transaction-item', 4),
    ]
    return {
      nodes: [a, b, store, cdc, ...sinks],
      edges: [
        { from: a.id, to: b.id },
        { from: b.id, to: store.id, ratePerMin: b.ratePerMin, status: b.status },
        { from: store.id, to: cdc.id },
        ...fanOut([cdc], sinks),
      ],
    }
  }, [t, goTopic])

  // { journey-fg, journey-bg, journey-updates } → fh_journey_service → channels → campaign-events
  const journeyGraph = useMemo(() => {
    const fg = topicNode(jny(t, 'journey-fg'), 'journey-fg-events', 0, goTopic)
    const bg = topicNode(jny(t, 'journey-bg'), 'journey-bg-events', 0, goTopic)
    // journey-updates is cache invalidation — it reaches the service but never the channels
    const updates = {
      ...topicNode(jny(t, 'journey-updates'), 'journey-updates', 0, goTopic),
      sublabel: 'cache invalidation',
    }
    const svc: FlowNodeSpec = {
      id: 'fh_journey_service', label: 'fh_journey_service', col: 1,
      kind: 'store', sublabel: 'journey engine',
    }
    const inputEdges = fanOut([fg, bg, updates], [svc])

    if (!demo) {
      // Everything past the journey engine is produced outside this repo — no group to poll.
      const downstream: FlowNodeSpec = {
        id: 'journey-downstream', label: 'j* → delivery → status', col: 2, kind: 'placeholder',
      }
      const campaignRaw = topicNode(raw(t, 'campaign'), 'campaign-raw', 2, goTopic)
      const campaignEvents = topicNode(evt(t, 'campaign'), 'campaign-events', 3, goTopic)
      const sinks = [
        connNode(gcs(t, 'events'), 'gcs-sink-events', 4),
        connNode(sr(t, 'campaign-events'), 'starrocks-sink-campaign-events', 4),
      ]
      return {
        nodes: [fg, bg, updates, svc, downstream, campaignRaw, campaignEvents, ...sinks],
        edges: [
          ...inputEdges,
          { from: svc.id, to: downstream.id },
          { from: downstream.id, to: campaignEvents.id },
          { from: campaignRaw.id, to: campaignEvents.id },
          ...fanOut([campaignEvents], sinks),
        ],
      }
    }

    const queues = JOURNEY_DEMO.queues.map(q => demoNode(q, 2, goTopic))
    const delivery = JOURNEY_DEMO.deliveryTopics.map(d => demoNode(d, 3, goTopic))
    const messageStatus = demoNode(JOURNEY_DEMO.messageStatus, 4, goTopic)
    const campaignRaw = topicNode(raw(t, 'campaign'), 'campaign-raw', 4, goTopic)
    const campaignEvents = topicNode(evt(t, 'campaign'), 'campaign-events', 5, goTopic)
    const sinks = [
      connNode(gcs(t, 'events'), 'gcs-sink-events', 6),
      connNode(sr(t, 'campaign-events'), 'starrocks-sink-campaign-events', 6),
    ]

    return {
      nodes: [fg, bg, updates, svc, ...queues, ...delivery, messageStatus,
              campaignRaw, campaignEvents, ...sinks],
      edges: [
        ...inputEdges,
        ...fanOut([svc], queues),
        // queue i feeds delivery topic i — same channel, so pair them positionally
        ...queues.map((q, i) => ({ from: q.id, to: delivery[i].id })),
        ...fanOut(delivery, [messageStatus]),
        { from: messageStatus.id, to: campaignEvents.id },
        { from: campaignRaw.id, to: campaignEvents.id },
        ...fanOut([campaignEvents], sinks),
      ],
    }
  }, [t, demo, goTopic])

  // datalist-raw → datalist-events → { GCS, StarRocks }
  const datalistGraph = useMemo(() => {
    const a = topicNode(raw(t, 'datalist'), 'datalist-raw', 0, goTopic)
    const b = topicNode(evt(t, 'datalist'), 'datalist-events', 1, goTopic)
    const sinks = [
      connNode(gcs(t, 'events'), 'gcs-sink-events', 2),
      connNode(sr(t, 'datalist-events'), 'starrocks-sink-datalist-events', 2),
    ]
    return { nodes: [a, b, ...sinks], edges: [{ from: a.id, to: b.id }, ...fanOut([b], sinks)] }
  }, [t, goTopic])

  // customer-raw → customer-update-events → { GCS, StarRocks }
  const customerGraph = useMemo(() => {
    const a = topicNode(raw(t, 'customer'), 'customer-raw', 0, goTopic)
    const b = topicNode(evt(t, 'customer-update'), 'customer-update-events', 1, goTopic)
    const sinks = [
      connNode(gcs(t, 'customer'), 'gcs-sink-customer', 2),
      connNode(sr(t, 'customer'), 'starrocks-sink-customer', 2),
    ]
    return { nodes: [a, b, ...sinks], edges: [{ from: a.id, to: b.id }, ...fanOut([b], sinks)] }
  }, [t, goTopic])

  const canvasProps = { asOfMs, paused }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-500" />
            Pipeline Health
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">End-to-end ingestion health per pipeline · 30s refresh</p>
        </div>
        <div className="flex items-center gap-3">
          {!demo && dataUpdatedAt && (
            <span className="text-xs text-gray-400">updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
          )}
          {health.failed > 0 && (
            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
              {health.failed} connector{health.failed !== 1 ? 's' : ''} need attention
            </span>
          )}
          {health.unknown > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium"
              title="Kafka Connect did not answer the status call — these connectors may well be running. Check PIPELINE_KAFKA_CONNECT_URL and Connect REST reachability.">
              {allUnknown ? 'Connector status unavailable' : `${health.unknown} connector status unknown`}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => setPaused(v => !v)}
            title={paused ? 'Resume flow animation' : 'Pause flow animation'}>
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm"
            onClick={() => { setDemo(v => !v); setDemoAsOf(Date.now()) }}
            className={cn(demo && 'border-amber-400 text-amber-600 bg-amber-50')}>
            <FlaskConical className="h-4 w-4 mr-1" />
            {demo ? 'Demo on' : 'Demo'}
          </Button>
          {!demo && (
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={cn('h-4 w-4 mr-1', isLoading && 'animate-spin')} />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {!demo && isLoading && <LoadingState />}
      {!demo && isError && (
        <ErrorState message={error instanceof Error ? error.message : 'Failed to load topology'} onRetry={() => refetch()} />
      )}

      {t && (
        <>
          {/* ── Cluster summary ── */}
          <div className="flex flex-wrap gap-4">
            <StatCard label="Throughput" value={`${fmtNum(totalRatePerMin / 60)}/s`}
              sub={`${fmtNum(totalRatePerMin)}/min across all sinks`} variant="ok" />
            <StatCard label="Total lag" value={totalLag}
              sub="messages behind, all stages"
              variant={totalLag > 1000 ? 'warn' : 'default'} />
            <StatCard label="Connectors"
              value={allUnknown ? `—/${health.total}` : `${health.running}/${health.total}`}
              sub={health.unknown === 0
                ? 'running'
                : allUnknown
                  ? 'status unavailable'
                  : `running · ${health.unknown} unknown`}
              variant={health.failed > 0 ? 'error' : health.unknown > 0 ? 'warn' : 'ok'}
              onClick={() => setConnectorsOpen(true)} />
            <StatCard label="Files queued" value={t.fileService.notStartedCount + t.fileService.inProgressCount}
              sub={t.fileService.inProgressOldestAgeSec != null
                ? `oldest ${Math.round(t.fileService.inProgressOldestAgeSec / 60)}m in progress`
                : 'nothing in progress'}
              variant={t.fileService.notStartedCount > 10 ? 'warn' : 'default'} />
            <StatCard label="File errors (24h)" value={t.fileService.errorCount24h}
              sub={`${t.fileService.doneCount24h} completed`}
              variant={t.fileService.errorCount24h > 0 ? 'error' : 'ok'} />
          </div>

          <FlowLegend />

          {/* ── Custom Event Ingestion ── */}
          <PipelineShell title="Custom Event Ingestion">
            <FlowCanvas {...canvasProps} nodes={customGraph.nodes} edges={customGraph.edges} />
          </PipelineShell>

          {/* ── Entity Ingestion ── */}
          <PipelineShell
            title="Entity Ingestion"
            badge={<span className="text-[11px] text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
              entity-update-group → MongoDB → CDC → GCS / SR entity connectors
            </span>}
          >
            <FlowCanvas {...canvasProps} nodes={entityGraph.nodes} edges={entityGraph.edges} />
          </PipelineShell>

          {/* ── Journey Events → Campaign Events ── */}
          <PipelineShell
            title="Journey Events → Campaign Events"
            badge={<span className="text-[11px] text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
              journey-updates is cache invalidation · campaign-raw also feeds in
            </span>}
          >
            <FlowCanvas {...canvasProps} nodes={journeyGraph.nodes} edges={journeyGraph.edges} />
          </PipelineShell>

          {/* ── Data List Event ── */}
          <PipelineShell title="Data List Event">
            <FlowCanvas {...canvasProps} nodes={datalistGraph.nodes} edges={datalistGraph.edges} />
          </PipelineShell>

          {/* ── Customer Raw ── */}
          <PipelineShell title="Customer Raw">
            <FlowCanvas {...canvasProps} nodes={customerGraph.nodes} edges={customerGraph.edges} />
          </PipelineShell>

          <p className="text-[11px] text-gray-400 text-right">
            {demo ? 'Demo mode — no live data' : `Snapshot: ${new Date(t.asOf).toLocaleString()}`}
          </p>

          {connectorsOpen && (
            <ConnectorListModal connectors={allConnectors} onClose={() => setConnectorsOpen(false)} />
          )}
        </>
      )}
    </div>
  )
}
